<script setup lang="ts">
import { computed, onMounted, ref, type PropType } from 'vue'
import { i18n } from '@/i18n'
import { Carousel, Slide } from 'vue3-carousel'
import useStrapiStore from '@/composition/strapiStore'
import useAppStore from '@/composition/appStore'
import type { LikeableResource, LikeableResourceGroup, Page, Project, Resource } from '@/models'
import ProjectCard from '@/components/resources/ProjectCard.vue'
import ResourceHeader from '@/components/resources/ResourceHeader.vue'
import BlockHeader from '@/components/blockStyles/fields/BlockHeader.vue'
import SelectedItems from '@/components/resources/SelectedItems.vue'
import CreateProjectDialog from '@/components/resources/CreateProjectDialog.vue'
import EmptyCollection from '@/components/EmptyCollection.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import RightArrow from '@/assets/rightArrow.svg'
import { useRouter } from 'vue-router'
import LoggedOut from '@/components/LoggedOut.vue'

const t = i18n.global.t
const router = useRouter()
const strapiStore = useStrapiStore()
const appStore = useAppStore()

defineProps({
  page: {
    type: Object as PropType<Page>,
    required: false,
  },
})

async function getResources() {
  await strapiStore.actions.loadResourceGroups()
  await strapiStore.actions.loadResources()
  await strapiStore.actions.loadCuratedProjects()
}

// All Resources and Projects to choose from
const resourceGroups = strapiStore.getters.resourceGroups
const resources = strapiStore.getters.resources
const projects = appStore.getters.projects
const curatedProjects = strapiStore.getters.curatedProjects
const user = appStore.getters.user

const carouselConfig = computed(() => ({
  itemsToShow: 1,
  wrapAround: true,
  gap: 40,
  breakpoints: {
    640: {
      // sm
      itemsToShow: 1.5,
    },
    768: {
      // md
      itemsToShow: 2.5,
    },
    1024: {
      // lg
      itemsToShow: 3.5,
    },
  },
}))

const tabs = [{ key: 'allProjects' }, { key: 'savedResources' }, { key: 'savedCases' }]

// Submenu
const currentTab = ref(0)

// Create
const createDialogActive = ref(false)
const existingProjectNames = computed(() => projects.value.map((p) => p.title))

// Project
const selectedProject = appStore.getters.currentProject

function createProject(name: string, description: string) {
  const p: Project = {
    id: '',
    title: name,
    description: description,
    lastEdited: new Date(),
    resources: [],
    whatFreeText: '',
  }
  appStore.actions.createProject(p)
  appStore.actions.setCurrentProject(p)
}

onMounted(() => {
  getResources()
  //if (projects.value.length) appStore.actions.setCurrentProject(projects.value[0]!)
  //else createProject(t('firstProjectName'), '')
})

function selectProject(project?: Project) {
  appStore.actions.setCurrentProject(project)
  scrollToSection()
}
function editProject(project?: Project) {
  appStore.actions.setCurrentProject(project)
  router.push('build')
}
function removeProject(id: string) {
  if (user.value?.likedCases.includes(id)) {
    appStore.actions.toggleResource('case', id)
  } else {
    appStore.actions.deleteProject(id)
  }
}

// Discover all resources within resource groups that have been built for the selected project
const builtResourceList = computed(() => {
  if (!selectedProject.value) return []
  return Array.from(resourceGroups.value.values()).map((rg) => {
    const resources: LikeableResource[] = rg.resources
      .filter((r) => selectedProject.value!.resources.includes(r.documentId))
      .map((r) => ({
        ...r,
        liked: true,
      }))
    const brg: LikeableResourceGroup = {
      ...rg,
      resources,
      liked: true,
    }
    return brg
  })
})

const likedCaseList = computed(() => {
  return curatedProjects.value.filter((c) => user.value?.likedCases.includes(c.id))
})

const likedResourceList = computed(() => {
  const list = user.value?.likedResources ?? []
  const allFoundResourcesAsGroups: LikeableResourceGroup[] = []
  const resourcesUsed: string[] = []

  // First iterate over resources inside groups created for DIY
  Array.from(resourceGroups.value.values()).forEach((rg) => {
    const resources: LikeableResource[] = rg.resources
      .filter((r) => list.includes(r.documentId))
      .map((r) => ({
        ...r,
        liked: true,
      }))
    resources.forEach((r) => resourcesUsed.push(r.documentId))
    const brg: LikeableResourceGroup = {
      ...rg,
      resources,
      liked: true,
    }
    allFoundResourcesAsGroups.push(brg)
    resourcesUsed.push(brg.documentId)
  })

  // Next iterate over resources without a group and add them to one to be used inside SelectedItems component
  const extraResources = Array.from(resources.value.values())
    .filter((r) => !resourcesUsed.includes(r.documentId))
    .filter((r) => list.includes(r.documentId))
    .map((r) => ({ ...r, liked: true }))
  const brg: LikeableResourceGroup = {
    createdAt: '',
    updatedAt: '',
    classification: 'topic',
    url: '',
    documentId: 'none',
    case: '',
    resources: extraResources,
    liked: true,
  }
  allFoundResourcesAsGroups.push(brg)
  return allFoundResourcesAsGroups
})

// Display all resources or cases that have been 'liked' depending on tab selected
/* const likedResourceOrCaseListOLD = computed(() => {
  const list =
    currentTab.value === 1 ? (user.value?.likedResources ?? []) : (user.value?.likedCases ?? [])
  return Array.from(resources.value.values()).filter((r) => list.includes(r.documentId))
}) */

const resourcesForProject = computed(() => {
  return (project: Project) => {
    const resourceList: Resource[] = []
    project.resources.forEach((r) => {
      const resource = resources.value.get(r)
      if (resource) {
        resourceList.push(resource)
      } else {
        console.log('Could not find resource ' + r)
      }
    })
    return resourceList
  }
})

function likeCase(cID: string) {
  appStore.actions.toggleResource('case', cID)
}

const section = ref<HTMLElement | null>(null)

const scrollToSection = () => {
  section.value?.scrollIntoView({ behavior: 'smooth' })
}
const loggedIn = computed(() => {
  return !!appStore.getters.user.value
})
</script>

<template>
  <div class="relative flex flex-col text-black">
    <!-- Creation dialog box -->
    <CreateProjectDialog
      v-model="createDialogActive"
      :existing-project-names="existingProjectNames"
      @create="createProject"
    />

    <!-- Search panel -->
    <ResourceHeader :logged-in="loggedIn" @create-project="createDialogActive = true">
      <template #heading>
        {{ t('yourImpact1') }}&nbsp;<span class="text-textYellow"
          >{{ t('oneFavourite') }}&nbsp;</span
        >{{ t('yourImpact2') }}
      </template>
      <template #subtitle>{{ t('favouriteDescription') }}</template>
    </ResourceHeader>

    <!-- content panel -->
    <div class="text-[#123A35] font-alexandria" ref="section">
      <div class="max-w-[1512px] sm:px-[5rem] max-sm:px-[2.5rem] mx-auto">
        <div class="m-8">
          <BlockHeader class="my-10 text-[#380525]" :text="t('myFavoritesHeader')" />
        </div>
        <!-- header -->
        <div v-if="loggedIn" class="m-8 flex flex-row border-b-3 border-grey py-4">
          <div class="flex flex-col sm:flex-row items-end max-sm:w-full">
            <p
              v-for="(tab, index) in tabs"
              :key="tab.key"
              class="cursor-pointer max-sm:rounded-full max-sm:px-4 max-sm:py-2 max-sm:w-full sm:mr-8 relative after:content-[''] after:absolute after:left-0 after:-bottom-[19px] after:w-full after:h-[3px]"
              :class="{
                'after:bg-black max-sm:after:bg-transparent sm:text-2xl text-black max-sm:bg-black max-sm:text-white font-normal':
                  currentTab === index,
                'after:bg-transparent sm:text-xl text-grey font-light': currentTab !== index,
              }"
              @pointerdown="currentTab = index"
            >
              {{ t(tab.key) }}
            </p>
          </div>
        </div>

        <!-- Tab 0: Project grid list or expanded project details view -->
        <div v-if="currentTab === 0" class="">
          <LoggedOut v-if="!loggedIn" />
          <EmptyCollection v-else-if="projects.length === 0" />
          <!-- Project List -->
          <div
            v-else-if="!selectedProject"
            class="grid grid-cols-2 lg:grid-cols-3 max-sm:grid-cols-1 mx-3 gap-10"
          >
            <ProjectCard
              v-for="p of projects"
              :key="p.id"
              class="rounded-lg cursor-pointer"
              :project="p"
              :resources="resourcesForProject(p)"
              :mode="'menu'"
              @select="selectProject"
              @edit="editProject"
              @delete="removeProject"
            />
          </div>

          <!-- Selected project details-->
          <div v-else-if="selectedProject" class="flex flex-col">
            <div class="m-8">
              <h2 class="text-2xl">{{ selectedProject.title }}</h2>
              <p>{{ selectedProject.description }}</p>
            </div>

            <!-- User-Selected Items per major category -->
            <SelectedItems :liked-resource-list="builtResourceList" :mode="'build'" />

            <div class="flex items-center justify-between m-8">
              <div
                @click="selectProject()"
                class="w-12 h-12 rounded-full flex items-center justify-center cursor-pointer bg-[#042E2C]"
              >
                <img :src="RightArrow" class="w-6 h-6 invert rotate-180" />
              </div>
              <button
                class="p-4 py-2 bg-[#042E2C] text-white w-fit self-end"
                @click="editProject(selectedProject)"
              >
                {{ t('editProject') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Tab 1: User's saved resources organized by collection -->
        <div v-else-if="currentTab === 1">
          <SelectedItems
            :liked-resource-list="likedResourceList"
            :like-mode="'like'"
            :mode="'regular'"
          />
        </div>

        <!-- Tab 2: Curated projects user has favorited -->
        <div v-else-if="currentTab === 2">
          <EmptyCollection v-if="likedCaseList.length === 0" />
          <div v-else class="grid grid-cols-2 lg:grid-cols-3 max-sm:grid-cols-1 mx-3 gap-10">
            <ProjectCard
              v-for="lc in likedCaseList"
              :key="lc.id"
              :resources="resourcesForProject(lc)"
              :project="lc"
              :liked="true"
              :mode="'menu'"
              @delete="removeProject"
            />
          </div>
        </div>

        <!-- Curated Projects Carousel list -->
        <div class="mt-20">
          <h2 class="text-3xl m-8">{{ t('curatedProjects') }}</h2>

          <Carousel
            ref="carousel"
            v-bind="carouselConfig"
            class="flex flex-row relative left-1/2 right-1/2 -ml-[50vw]"
          >
            <Slide v-for="(project, index) in curatedProjects" :key="project.id">
              <ProjectCard
                class="max-w-96 max-sm:mx-12"
                :highlight="[
                  ['bg-[#006E6821]', 'bg-[#93B5CE4F]', 'bg-[#AB659C38]', 'bg-[#FED0462E]'][
                    index % 4
                  ]!,
                ]"
                :resources="resourcesForProject(project)"
                :project="project"
                :liked="user?.likedCases.includes(project.id)"
                :mode="loggedIn ? 'like' : 'none'"
                @like="likeCase"
                @select="selectProject"
              />
            </Slide>
          </Carousel>
        </div>
      </div>
      <SiteFooter class="mt-32" />
    </div>
  </div>
</template>

<style scoped></style>

<style>
.carousel {
  max-width: 100dvw;
}
.carousel .carousel__track {
  padding-bottom: 8px;
}
</style>
